import { EMEARegion, LARegion, NARegion } from "@dana/Common/Constants";
import ExpandMore from "@dana/Icons/ExpandMore";
import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import setLanguage from "@dana/Overrides/Store/Context/Handlers/SetLanguage";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { LanguageCollectionModel, LanguageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";


const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};
interface ChevronIconProps {
    isOpen: boolean;
}
interface LanguageOptionProps {
    selected?: boolean;
}
export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageToggleWrapper?: InjectableCss;
    languageOptionWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageRegionTitle?: TypographyPresentationProps;
    languageRegionHeading?: TypographyPresentationProps;
    dropDownArrowLanguageSelector?: TypographyPresentationProps;
    languageSelect?: SelectPresentationProps;
}


export const StyledToggleButton = styled.div`
  padding: 4px 32px 4px 8px;
  border: none;
  background-color: transparent;
  display: flex;
  letter-spacing: .1rem;
  align-items: center;
  cursor: pointer;
  outline: none;

  &:hover,
  &:focus,
  &:focus-within {
    color: ${themeColors.secondary.danaBrightBlue};
  }
`;

export const LanguageOption = styled.li<LanguageOptionProps>`
  text-transform: capitalize;
  padding: 4px 8px;
  width: 100%;
  cursor: pointer;
  white-space: nowrap;
  color: ${themeColors.primary.danaBlue};
  background-color: transparent;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus {
    background-color: ${themeBackgrounds.offWhite};
    color: ${themeColors.secondary.danaDarkBlue};;
    outline: none;
  }

    ${({ selected }) =>
        selected &&
        css`
      background-color: ${themeBackgrounds.lightGray};
      color: ${themeColors.primary.richBlack};
    `}
`;

export const DropdownMenu = styled.ul`
  color: ${themeColors.primary.richBlack};
  z-index: 1000;
  list-style: none;
  margin: 0;
  min-width: 100%;
  flex-basis: 100%;
  gap: 8px;
  width: 100%;
  padding-left: 0;
  display: flex;
  flex-direction: column;
`;

const ChevronIcon = styled.div<ChevronIconProps>`
  display: inline-flex;
  transition: transform 0.3s ease;

  ${({ isOpen }) =>
        isOpen &&
        css`
      transform: rotate(180deg);
    `}
`;

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            display: flex;
            align-items: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            &:focus,
            &:focus-visible {
                outline: none;
                border: 1px solid ${themeColors.primary.white};
                box-shadow: ${elevation.focus};
                outline-offset: 1px;
            }
            
             &:focus-within {
              
                .language-icon {
                 svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }

              
                .language-toggle-button {
                color: ${themeColors.secondary.danaBrightBlue};
                }

              
                .dropdown-arrow {
                 svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }
            }

            &:focus-visible {
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }

                select:not(.react-datetime-picker__inputGroup__input) {
                    color: ${themeColors.secondary.danaBrightBlue};
                    border: 1px solid ${themeColors.primary.white};
                    box-shadow: ${elevation.focus};
                }
            }

            &:hover {
                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }

                select:not(.react-datetime-picker__inputGroup__input) {
                    color: ${themeColors.secondary.danaBrightBlue} !important;
                }
            }
        `,
    },
    languageToggleWrapper: {
        css: css`
            position: relative;
            cursor: pointer;
            text-transform: uppercase;
            color: ${themeColors.primary.white}; 
        `,
    },
    languageOptionWrapper: {
        css: css`
            position: absolute;
            top: 35px;
            right: 0;
            background-color: ${themeColors.primary.white};
            color: ${themeColors.primary.richBlack};
            z-index: 1000;
            list-style: none;
            text-transform: capitalize;
            margin: 0;
            min-width: 100%;
            gap: 8px;
            display: flex;
            width: 300px;
            padding: 8px;
            flex-direction: column;
            align-items: flex-start;
            box-shadow: ${elevation.hover};
        `
    },
    languageRegionTitle: {
        css: css`
            padding: 8px;
        `
    },
    languageRegionHeading: {
        css: css`
            display: block;
            padding: 4px 8px;
            color: ${themeColors.primary.richBlack};
        `,
    },
    dropDownArrowLanguageSelector: {
        css: css`
        position: absolute;
        right: 0;
        top: 60%; 
        transform: translateY(-50%);
        color: ${themeColors.primary.white};

        [class*="IconWrapper"] {
            svg {
                path {
                    fill: ${themeColors.primary.white};
                }
            }
        }
        `
    },

    languageIcon: {
        size: 22,
        src: Globe,
    },
    languageSelect: {
        mergeCss: true,
        backgroundColor: "transparent",
        iconProps: {
            color: themeColors.primary.white,
            css: css`
                svg {
                    path {
                        fill: ${themeColors.primary.white};
                    }
                }
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: auto;

                ${IconWrapper} {
                    top: 50%;
                    transform: translateY(-50%);
                }
            `,
            inputSelect: css`
                text-transform: uppercase;

                &:not(.react-datetime-picker__inputGroup__input) {
                    font-size: ${fontSizes.small};
                    line-height: ${lineHeights.defaultMedium};
                    font-weight: ${fontWeights.medium};
                    &,
                    &:focus,
                    &:active,
                    &:focus-visible {
                        padding: 0 32px 0 8px;
                        border: none;
                        color: ${themeColors.primary.white};
                    }

                    option {
                        color: ${themeColors.primary.richBlack};
                    }
                }
            `,
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

export const LogoImage = styled.img`
    height: 22px;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    if (!languages || languages.length <= 1) {
        return null;
    }
    const regionWiseData: { [region: string]: LanguageModel[] } = {};

    for (const lang of languages) {
        const region = lang.properties.region || "Unknown";
        if (!regionWiseData[region]) {
            regionWiseData[region] = [];
        }        
        regionWiseData[region].push(lang);
    }

    const styles = useMergeStyles("languageMenu", languageMenuStyles);
    const menuId = "languageMenu";

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    const toggleDropdown = () => setIsOpen(prev => !prev);
    const handleSelect = (languageId: string) => {
        setLanguage({ languageId });
        setIsOpen(false);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedLanguage = languages.find(l => l.id === currentLanguage?.id);
    
    return (
        <StyledWrapper {...styles.languageWrapper}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDropdown();
                }
            }}
        >
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage src={currentLanguage.imageFilePath} alt="" />
                ) : (
                    <Icon className="language-icon" {...styles.languageIcon} />
                ))}

            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Language")}
            </VisuallyHidden>

            <StyledWrapper
                ref={dropdownRef}
                id={menuId}
                className="custom-language-dropdown"

                {...styles.languageToggleWrapper}
            >
                <StyledToggleButton
                    onClick={toggleDropdown}

                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby={`${menuId}-label`}
                    className="language-toggle-button"
                >
                    {selectedLanguage?.properties?.region && selectedLanguage?.description
                        ? `${selectedLanguage.properties.region} | ${selectedLanguage.description?.replace('0','').slice(0, 2)}`
                        : translate("Select Language")}
                    <Typography {...styles.dropDownArrowLanguageSelector}>
                        <ChevronIcon isOpen={isOpen}>
                            <Icon className="dropdown-arrow" src={ExpandMore} />
                        </ChevronIcon>
                    </Typography>
                </StyledToggleButton>
                {isOpen && (
                    <StyledWrapper
                        {...styles.languageOptionWrapper}
                    >
                        <Typography {...styles.languageRegionTitle} className="text-body-lg">
                            {translate("Region & Language")}
                        </Typography>

                        <DropdownMenu role="listbox" aria-labelledby={`${menuId}-label`}>
                            {Object.entries(regionWiseData)
                            .sort((a, b) => b[0].localeCompare(a[0]))
                            .map(([region, langs], idx, array) => (
                                <li key={region} style={{ width: "100%", borderBottom: idx !== array.length - 1 ? `1px solid ${themeBackgrounds.lightGray}` : "none", }}>
                                    <Typography {...styles.languageRegionHeading}>
                                        {region === NARegion ? translate("North America") : region === LARegion ? translate("Latin America") : region === EMEARegion ? translate("Europe | Middle East | Africa") : region}
                                    </Typography>
                                    <ul style={{ margin: 0 }}>
                                        {langs.map((lang) => (
                                            <LanguageOption
                                                key={lang.id}
                                                onClick={() => handleSelect(String(lang.id))}
                                                tabIndex={0}
                                                role="option"
                                                aria-selected={selectedLanguage?.id === lang.id}
                                                selected={selectedLanguage?.id === lang.id}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        handleSelect(String(lang.id));
                                                    }
                                                }}
                                                className="text-body-sm"

                                            >
                                                {lang.description?.replace('0','')}
                                            </LanguageOption>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </DropdownMenu>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};


const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
